import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Badge } from '../components/ui/badge';
import { 
  Calculator, DollarSign, ArrowRight, CheckCircle2, 
  FileText, HelpCircle, TrendingUp, PieChart, BarChart3
} from 'lucide-react';
import { motion } from 'framer-motion';
import CryptoTaxCalculatorTool from '../components/tools/stage1/CryptoTaxCalculatorTool';
import { TaxPageMonetization, TaxReportEmailCapture } from '../components/TaxPageMonetization';

const CryptoProfitTaxCalculator = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I calculate tax on crypto profits?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Calculate your profit (sale price minus cost basis), then multiply by your applicable tax rate. For example: if you bought crypto for $5,000 and sold for $8,000, your profit is $3,000. At a 15% long-term rate, you owe $450 in tax."
        }
      },
      {
        "@type": "Question",
        "name": "What is the tax rate on crypto profits?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Tax rates vary by country and holding period. In the US, long-term gains (>1 year) are taxed at 0%, 15%, or 20%. Short-term gains are taxed as ordinary income (10-37%). UK rates are 10-20%. Germany offers 0% after 1 year."
        }
      },
      {
        "@type": "Question",
        "name": "Do I pay tax on unrealized crypto profits?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Unrealized profits (when you haven't sold) are not taxable in most countries. You only owe tax when you 'realize' the gain by selling, trading, or spending your crypto. However, some countries may have wealth taxes on holdings."
        }
      },
      {
        "@type": "Question",
        "name": "Can I offset crypto losses against profits?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, in most countries. Capital losses can offset capital gains. In the US, if losses exceed gains, you can deduct up to $3,000 against ordinary income annually. Remaining losses carry forward to future years."
        }
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Crypto Profit Tax Calculator (2026) - Calculate Gains Tax Free"
        description="Calculate taxes on cryptocurrency profits instantly. Free crypto profit tax calculator with 2026 rates. Estimate capital gains tax on Bitcoin, Ethereum, altcoin sales."
        keywords="crypto profit tax calculator, cryptocurrency gains tax, calculate crypto tax, crypto capital gains calculator, profit tax on crypto, crypto profit tax rate"
        canonical="https://toolsforgeapp.com/crypto-profit-tax-calculator"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="min-h-screen py-12 px-6" data-testid="crypto-profit-tax-calculator-page">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span>/</span>
            <Link to="/crypto-calculators" className="hover:text-primary transition-colors">Crypto Calculators</Link>
            <span>/</span>
            <span className="text-foreground">Profit Tax Calculator</span>
          </nav>

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <Badge variant="outline" className="mb-4 px-4 py-1.5 border-green-500/30 text-green-400">
              <TrendingUp className="w-3 h-3 mr-2" />
              2026 Tax Rates Applied
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-['Plus_Jakarta_Sans']">
              Crypto Profit Tax Calculator
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Turn your crypto gains into after-tax profit projections. Our free calculator estimates the tax you'll owe on cryptocurrency profits, helping you understand your true returns. Whether you made $500 or $500,000, calculate your capital gains tax instantly with country-specific 2026 rates.
            </p>
          </motion.div>

          {/* Visual Formula */}
          <div className="mb-10 p-6 rounded-xl border border-green-500/30 bg-green-500/5">
            <h2 className="text-lg font-semibold mb-4 text-center">Crypto Profit Tax Formula</h2>
            <div className="flex flex-wrap items-center justify-center gap-3 text-sm md:text-base">
              <div className="px-4 py-2 rounded-lg bg-blue-500/20 border border-blue-500/30">
                <span className="text-blue-400 font-semibold">Sale Price</span>
              </div>
              <span className="text-2xl">−</span>
              <div className="px-4 py-2 rounded-lg bg-purple-500/20 border border-purple-500/30">
                <span className="text-purple-400 font-semibold">Cost Basis</span>
              </div>
              <span className="text-2xl">=</span>
              <div className="px-4 py-2 rounded-lg bg-green-500/20 border border-green-500/30">
                <span className="text-green-400 font-semibold">Profit</span>
              </div>
              <span className="text-2xl">×</span>
              <div className="px-4 py-2 rounded-lg bg-amber-500/20 border border-amber-500/30">
                <span className="text-amber-400 font-semibold">Tax Rate</span>
              </div>
              <span className="text-2xl">=</span>
              <div className="px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/30">
                <span className="text-red-400 font-semibold">Tax Owed</span>
              </div>
            </div>
          </div>

          {/* Key Stats */}
          <div className="grid md:grid-cols-4 gap-4 mb-10">
            <div className="p-4 rounded-xl border border-border bg-card text-center">
              <BarChart3 className="w-6 h-6 mx-auto mb-2 text-blue-400" />
              <p className="text-2xl font-bold">0-37%</p>
              <p className="text-xs text-muted-foreground">US Tax Range</p>
            </div>
            <div className="p-4 rounded-xl border border-border bg-card text-center">
              <PieChart className="w-6 h-6 mx-auto mb-2 text-green-400" />
              <p className="text-2xl font-bold">15%</p>
              <p className="text-xs text-muted-foreground">Most Common Rate</p>
            </div>
            <div className="p-4 rounded-xl border border-border bg-card text-center">
              <TrendingUp className="w-6 h-6 mx-auto mb-2 text-purple-400" />
              <p className="text-2xl font-bold">$3,000</p>
              <p className="text-xs text-muted-foreground">US Loss Deduction</p>
            </div>
            <div className="p-4 rounded-xl border border-border bg-card text-center">
              <DollarSign className="w-6 h-6 mx-auto mb-2 text-amber-400" />
              <p className="text-2xl font-bold">1 Year</p>
              <p className="text-xs text-muted-foreground">Long-term Threshold</p>
            </div>
          </div>

          {/* Calculator Tool */}
          <div className="mb-8">
            <CryptoTaxCalculatorTool />
          </div>

          {/* Monetization Section - After Calculator */}
          <TaxPageMonetization 
            region="usa"
            complianceText="Ensure accurate profit reporting to avoid IRS penalties"
          />

          {/* How to Calculate Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <Calculator className="w-6 h-6 text-primary" />
              How to Calculate Tax on Crypto Profits
            </h2>
            
            <div className="prose prose-invert max-w-none">
              <p className="text-muted-foreground mb-4">
                Calculating tax on your crypto profits involves determining your gain and applying the correct rate. Here's a comprehensive breakdown:
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-sm text-blue-400">1</span>
                    Calculate Your Profit
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    <strong>Profit = Sale Price - Cost Basis</strong>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Your cost basis includes the purchase price plus any fees (exchange fees, gas fees, transfer costs).
                  </p>
                </div>
                
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-sm text-purple-400">2</span>
                    Determine Tax Rate
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    <strong>Based on holding period:</strong>
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Short-term (&lt;1 yr): Ordinary income rates</li>
                    <li>• Long-term (&gt;1 yr): Preferential rates</li>
                  </ul>
                </div>
                
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-sm text-green-400">3</span>
                    Calculate Tax Owed
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    <strong>Tax = Profit × Tax Rate</strong>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Example: $10,000 profit × 15% = $1,500 tax owed
                  </p>
                </div>
              </div>
              
              {/* Tax Rate Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm border border-border rounded-lg">
                  <thead className="bg-card">
                    <tr>
                      <th className="p-3 text-left border-b border-border">Country</th>
                      <th className="p-3 text-left border-b border-border">Short-Term Rate</th>
                      <th className="p-3 text-left border-b border-border">Long-Term Rate</th>
                      <th className="p-3 text-left border-b border-border">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="p-3">USA</td>
                      <td className="p-3 text-red-400">10-37%</td>
                      <td className="p-3 text-green-400">0-20%</td>
                      <td className="p-3 text-muted-foreground">1 year holding period</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="p-3">UK</td>
                      <td className="p-3 text-amber-400">10-20%</td>
                      <td className="p-3 text-amber-400">10-20%</td>
                      <td className="p-3 text-muted-foreground">£3,000 annual exempt</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="p-3">Germany</td>
                      <td className="p-3 text-red-400">Up to 45%</td>
                      <td className="p-3 text-green-400">0%</td>
                      <td className="p-3 text-muted-foreground">Tax-free after 1 year</td>
                    </tr>
                    <tr>
                      <td className="p-3">Bulgaria</td>
                      <td className="p-3 text-blue-400">10%</td>
                      <td className="p-3 text-blue-400">10%</td>
                      <td className="p-3 text-muted-foreground">Flat rate</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.section>

          {/* Example Calculations */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-xl font-bold mb-4 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-400" />
              Crypto Profit Tax Examples
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl border border-green-500/30 bg-green-500/5">
                <h3 className="font-semibold mb-3 text-green-400">Example 1: Small Profit ($500)</h3>
                <div className="font-mono text-sm space-y-1">
                  <p>Cost Basis: $2,000</p>
                  <p>Sale Price: $2,500</p>
                  <p className="text-green-400">Profit: $500</p>
                  <p>Tax Rate (15% long-term): 15%</p>
                  <p className="text-red-400 font-bold">Tax Owed: $75</p>
                </div>
              </div>
              
              <div className="p-5 rounded-xl border border-amber-500/30 bg-amber-500/5">
                <h3 className="font-semibold mb-3 text-amber-400">Example 2: Large Profit ($50,000)</h3>
                <div className="font-mono text-sm space-y-1">
                  <p>Cost Basis: $25,000</p>
                  <p>Sale Price: $75,000</p>
                  <p className="text-green-400">Profit: $50,000</p>
                  <p>Tax Rate (20% high earner): 20%</p>
                  <p className="text-red-400 font-bold">Tax Owed: $10,000</p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Email Capture - Before FAQ */}
          <TaxReportEmailCapture region="usa" />

          {/* FAQ Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-purple-400" />
              Crypto Profit Tax FAQs
            </h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "How do I calculate tax on crypto profits?",
                  a: "First, determine your profit: Sale Price minus Cost Basis (purchase price + fees). Then multiply by your tax rate. Example: $8,000 sale - $5,000 cost = $3,000 profit. At 15% long-term rate: $3,000 × 15% = $450 tax owed."
                },
                {
                  q: "What is the tax rate on crypto profits?",
                  a: "It depends on your country and holding period. US long-term rates (>1 year) are 0%, 15%, or 20% based on income. Short-term rates are 10-37%. UK charges 10-20%. Germany is 0% after 1 year. Bulgaria has a flat 10%."
                },
                {
                  q: "Do I pay tax on unrealized crypto profits?",
                  a: "No, in most countries. 'Unrealized' means you haven't sold yet. You only owe tax when you 'realize' the gain by selling, trading, or spending crypto. Your portfolio can grow tax-free until you dispose of assets."
                },
                {
                  q: "Can I offset crypto losses against profits?",
                  a: "Yes, capital losses offset capital gains. In the US, if losses exceed gains, you can deduct $3,000 per year against ordinary income. Unused losses carry forward indefinitely. This is called 'tax-loss harvesting.'"
                },
                {
                  q: "What is the difference between short-term and long-term crypto profit tax?",
                  a: "Short-term profits (held under 1 year) are taxed at higher ordinary income rates. Long-term profits (held over 1 year) qualify for lower capital gains rates. In the US, the difference can be significant: 37% vs 20% for high earners."
                }
              ].map((faq, index) => (
                <div key={index} className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    {faq.q}
                  </h3>
                  <p className="text-sm text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Related Tools */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-xl font-semibold mb-4 font-['Plus_Jakarta_Sans']">Related Tax Tools</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: 'Crypto Tax Calculator', slug: 'crypto-tax-calculator' },
                { name: 'Crypto Profit Calculator', slug: 'crypto-profit-calculator' },
                { name: 'Crypto ROI Calculator', slug: 'crypto-roi-calculator' },
                { name: 'All Crypto Tools', slug: '../crypto-calculators', isHub: true }
              ].map(tool => (
                <Link
                  key={tool.slug}
                  to={tool.isHub ? '/crypto-calculators' : `/tools/${tool.slug}`}
                  className="p-3 rounded-lg border border-border bg-card hover:border-primary/50 transition-all text-sm font-medium flex items-center justify-between"
                >
                  {tool.name}
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </motion.section>
        </div>
      </div>
    </>
  );
};

export default CryptoProfitTaxCalculator;
